import { PathsV1UserSearchPostParametersQuerySearchField, UserStatus, UserType } from "@/api-schema-enums";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { getUserDetailPath } from "@/common/helpers/get-user-detail-path";
import { useNavigationItem } from "@/common/hooks/use-navigation";
import { UserAvatar, UserAvatarSize } from "@/components/user-avatar/user-avatar";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo } from "react";
import type { PaletteCommand } from "../types";

const MIN_USER_SEARCH_LENGTH = 2;
const MAX_USER_RESULTS = 5;
const USER_SEARCH_DEBOUNCE_MS = 250;
const USER_SEARCH_FIELDS = Object.values(PathsV1UserSearchPostParametersQuerySearchField);

export function useUserSearchCommands(search: string): {
  commands: PaletteCommand[];
  isLoading: boolean;
} {
  const phonebook = useNavigationItem("phonebook");
  const term = search.trim();
  const debouncedTerm = useDebounce(term, USER_SEARCH_DEBOUNCE_MS);
  const canSearch = !!phonebook && term.length >= MIN_USER_SEARCH_LENGTH;
  const enabled = canSearch && debouncedTerm.length >= MIN_USER_SEARCH_LENGTH;

  const { data, isSuccess, isPending } = useUserSearchQuery({
    options: {
      parameters: {
        search: debouncedTerm,
        searchField: USER_SEARCH_FIELDS,
        pageSize: MAX_USER_RESULTS,
        includeCount: false,
      },
      body: { type: [UserType.Employee], status: [UserStatus.Enabled] },
    },
    enabled,
  });
  const isCurrentTerm = term === debouncedTerm;
  // Results are only shown once they belong to the current input, so stale people can never be selected.
  const isUpToDate = enabled && isCurrentTerm && isSuccess;

  const commands = useMemo(() => {
    if (!isUpToDate) return [];

    return (data?.data ?? []).map((user): PaletteCommand => {
      const name = getFullUserName({ user });

      return {
        id: user.uuid,
        title: name,
        subtitle: user.email ?? undefined,
        icon: <UserAvatar userAzureObjectUuid={user.azureObjectUuid} name={name} size={UserAvatarSize.Xs} />,
        to: getUserDetailPath(user.uuid).slice(1),
      };
    });
  }, [isUpToDate, data]);

  return { commands, isLoading: canSearch && (!isCurrentTerm || isPending) };
}
